import BottomNavUtils from '@/modules/layout/bottomNav/utils/bottomNavUtils';
import bottomNavItems from '@/modules/layout/bottomNav/bottomNavItems';
import {
  buildMobilePlatformBottomTabs,
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES,
} from '@/modules/layout/platformNavigation/utils/mobilePlatformBottomTabs';
import {
  buildPathSet,
  filterBottomNavTabsByPathSet,
  getBottomNavTabPaths,
  normalizeMobileNavigationPath,
} from '@/modules/layout/platformNavigation/utils/mobileNavigationDuplicateFiltering';

const MOBILE_GLASSY_NAV_FLAG = 'rollout.platform_administration.mobile_glassy_nav_layout';
const MOBILE_PLATFORM_NAV_FLAG = 'rollout.platform_administration.mobile_platform_nav';

const isMobilePlatformNavigationActive = (rootGetters) => {
  const hasFeature = rootGetters['BusinessStore/hasFeature'];
  return hasFeature?.(MOBILE_GLASSY_NAV_FLAG) && hasFeature?.(MOBILE_PLATFORM_NAV_FLAG);
};

const hasInboxTab = (tabs) => tabs.some((tab) => tab.name === 'inbox');
const ACTION_ONLY_MOBILE_PLATFORM_TAB_TYPES = [
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_MENU,
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_GRID,
];

const isActionOnlyMobilePlatformTab = (tab) => ACTION_ONLY_MOBILE_PLATFORM_TAB_TYPES.includes(tab?.actionType);

// Action-only tabs open another mobile navigation surface instead of representing the current route.
// Keeping them selected blocks tapping them again, so clear the selection after the action runs.
const shouldClearCurrentIndex = (tabs, currentIndex) => {
  const bottomNavTabs = tabs || [];
  if (!Number.isInteger(currentIndex) || currentIndex < 0 || bottomNavTabs.length === 0) {
    return false;
  }

  const currentTab = bottomNavTabs[currentIndex];
  return !currentTab || isActionOnlyMobilePlatformTab(currentTab);
};

const getCurrentRouteTabIndex = (tabs, currentRoutePath) => {
  const normalizedRoutePath = normalizeMobileNavigationPath(currentRoutePath);
  if (!normalizedRoutePath) return -1;

  return (tabs || []).findIndex((tab) => getBottomNavTabPaths(tab).includes(normalizedRoutePath));
};

const state = {
  bottomNavConfig: {
    show: true,
    bottomNavTabs: bottomNavItems,
    features: [],
    permissions: [],
    currentIndex: undefined,
  },
  isLoading: true,
};

const mutations = {
  setShow(state, show) {
    state.bottomNavConfig.show = show;
  },
  setBottomNavTabs(state, tabs) {
    state.bottomNavConfig.bottomNavTabs = tabs;
    state.isLoading = false;
  },
  setCurrentIndex(state, newIndex) {
    if (newIndex < 0) {
      state.bottomNavConfig.currentIndex = undefined;
    } else {
      state.bottomNavConfig.currentIndex = newIndex;
    }
  },
  setInboxTabConversationCounts(state, counter) {
    const inboxTabIndex = state.bottomNavConfig.bottomNavTabs.findIndex((tab) => tab.name === 'inbox');
    if (inboxTabIndex < 0) {
      return;
    }
    state.bottomNavConfig.bottomNavTabs[inboxTabIndex].badgeValue = counter;
  },
};

const getters = {
  bottomNavTabs: (state) => state.bottomNavConfig.bottomNavTabs,
  showBottomNav: (state) => state.bottomNavConfig.show,
  currentIndex: (state) => state.bottomNavConfig.currentIndex,
  isLoading: (state) => state.isLoading,
};

const actions = {
  async createBottomNavTabs({ rootGetters, commit, state: storeState = state }) {
    const fallbackTabs = BottomNavUtils.getAggregatedTabs(rootGetters);
    const activeItem = rootGetters['PlatformNavigationStore/activeTopBarItem'];
    const isMobilePlatformActive = isMobilePlatformNavigationActive(rootGetters);
    const filteredFallbackTabs = isMobilePlatformActive && activeItem?.isBMP
      ? filterBottomNavTabsByPathSet(
        fallbackTabs,
        buildPathSet(rootGetters['PlatformNavigationStore/nonBmpNavigationPaths']),
      )
      : fallbackTabs;
    const tabs = buildMobilePlatformBottomTabs({
      activeItem,
      fallbackTabs: filteredFallbackTabs,
      getItemTitle: rootGetters['PlatformNavigationStore/getItemTitle'],
      isMobilePlatformNavigationActive: isMobilePlatformActive,
    });
    commit('setBottomNavTabs', tabs);
    const currentRouteIndex = getCurrentRouteTabIndex(
      tabs,
      rootGetters['PlatformNavigationStore/currentRoutePath'],
    );
    if (currentRouteIndex >= 0) {
      commit('setCurrentIndex', currentRouteIndex);
    } else if (shouldClearCurrentIndex(tabs, storeState.bottomNavConfig.currentIndex)) {
      commit('setCurrentIndex', -1);
    }
  },
  setBottomNavConfig({ commit }, bottomNavConfig) {
    commit('setShow', bottomNavConfig.show);
  },
  setBottomNavStateChange({ commit }, stateName) {
    const index = BottomNavUtils.getTabIndexForAction(state, stateName);
    commit('setCurrentIndex', index);
  },
  setCurrentIndex({ commit, state: storeState = state }, index) {
    const tabs = storeState.bottomNavConfig.bottomNavTabs;
    commit('setCurrentIndex', shouldClearCurrentIndex(tabs, index) ? -1 : index);
  },
  async updateInboxTabConversationCounts({ state, rootGetters, dispatch, commit }) {
    if (!hasInboxTab(state.bottomNavConfig.bottomNavTabs)) {
      return;
    }

    await dispatch({ type: 'ConversationStore/fetchConversationCounts' }, { root: true });
    if (hasInboxTab(state.bottomNavConfig.bottomNavTabs)) {
      const counter = rootGetters['ConversationStore/counts'];
      commit('setInboxTabConversationCounts', counter);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
